import 'chart.js/auto';
import { useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import { displayDay } from '../dashboard-filters';
import { DashboardSummary } from '../services/responses/dashboard.response';
import { OTHER_SLOT, categoricalPalette, sourceSlot } from './chart-palette';
import { Panel, PanelState, Segmented, useDashboardColors } from './panel';

interface Props {
    data?: DashboardSummary;
    onPickDay: (day: string) => void;
}

type Mode = 'total' | 'source';

const shortDay = (iso: string) => displayDay(iso).slice(0, 5);

/// Entradas por dia; a linha tracejada é o período anterior, para dizer se o pico é pico mesmo.
export const VolumeChart = ({ data, onPickDay }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = useDashboardColors();
    const palette = categoricalPalette(controller.darkMode);

    const [mode, setMode] = useState<Mode>('total');
    // Onde o botão desceu: soltar depois de arrastar também dispara click no canvas.
    const pressedAt = useRef<{ x: number; y: number } | null>(null);

    const points = data?.totals.byDay ?? [];
    const previous = data?.previous.byDay ?? [];
    const total = points.reduce((sum, p) => sum + p.count, 0);

    // Cor fixa por origem; as que não têm cor própria somam em "Outros".
    const series = data?.bySourceDay ?? [];
    const own = series.filter((item) => sourceSlot(item.source) !== OTHER_SLOT);
    const rest = series.filter((item) => sourceSlot(item.source) === OTHER_SLOT);
    const sourceLines = [
        ...own.map((item) => ({ label: item.label, counts: item.counts, color: palette[sourceSlot(item.source)] })),
        ...(rest.length
            ? [{
                label: t('crmDashboard.outros'),
                counts: points.map((_, day) => rest.reduce((sum, item) => sum + (item.counts[day] ?? 0), 0)),
                color: palette[OTHER_SLOT],
            }]
            : []),
    ];

    const legend = mode === 'total'
        ? [
            { label: t('painel.periodoAtual'), color: cc.accent, dashed: false },
            data && {
                label: t('painel.periodoAnterior', { de: displayDay(data.previousFrom), ate: displayDay(data.previousTo) }),
                color: cc.textMuted,
                dashed: true,
            },
        ].filter(Boolean) as { label: string; color: string; dashed: boolean }[]
        : sourceLines.map((line) => ({ label: line.label, color: line.color, dashed: false }));

    const dense = points.length > 45;

    const datasets = mode === 'total'
        ? [
            {
                label: t('painel.periodoAtual'),
                data: points.map((p) => p.count),
                borderColor: cc.accent,
                backgroundColor: `${cc.accent}22`,
                borderWidth: 2,
                pointRadius: dense ? 0 : 3,
                pointHoverRadius: 5,
                tension: 0.25,
                fill: true,
            },
            {
                label: t('painel.anterior'),
                data: previous.map((p) => p.count),
                borderColor: cc.textMuted,
                borderDash: [5, 4],
                borderWidth: 1.5,
                pointRadius: 0,
                pointHoverRadius: 3,
                tension: 0.25,
                fill: false,
            },
        ]
        : sourceLines.map((line) => ({
            label: line.label,
            data: line.counts,
            borderColor: line.color,
            backgroundColor: line.color,
            borderWidth: 2,
            pointRadius: dense ? 0 : 2.5,
            pointHoverRadius: 5,
            tension: 0.25,
            fill: false,
        }));

    return (
        <Panel
            title={t('painel.volumeTitulo')}
            subtitle={t('painel.volumeAjuda')}
            action={
                <Segmented
                    value={mode}
                    options={[
                        { value: 'total', label: t('painel.total') },
                        { value: 'source', label: t('painel.porOrigem') },
                    ]}
                    onChange={setMode}
                />
            }
            footer={total > 0 ? t('painel.cliqueDia') : undefined}
        >
            {!data || total === 0 ? (
                <PanelState loading={!data} empty={t('crmDashboard.semDados')} height={240} />
            ) : (
                <>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                        {legend.map((item) => (
                            <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                <Box
                                    sx={{
                                        width: 16,
                                        borderTop: `2px ${item.dashed ? 'dashed' : 'solid'} ${item.color}`,
                                    }}
                                />
                                <Typography sx={{ color: cc.textSecondary, fontSize: '0.74rem' }}>{item.label}</Typography>
                            </Box>
                        ))}
                    </Box>
                    <Box
                        sx={{ height: 250 }}
                        onPointerDown={(event) => { pressedAt.current = { x: event.clientX, y: event.clientY }; }}
                    >
                        <Line
                            data={{ labels: points.map((p) => shortDay(p.day)), datasets }}
                            options={{
                                maintainAspectRatio: false,
                                interaction: { mode: 'index', intersect: false },
                                onClick: (event, elements) => {
                                    const native = event.native as MouseEvent | null;
                                    const start = pressedAt.current;
                                    const dragged = native && start
                                        && Math.hypot(native.clientX - start.x, native.clientY - start.y) > 5;
                                    const index = elements[0]?.index;
                                    // Dia sem lead filtraria o painel inteiro para vazio.
                                    if (!dragged && index !== undefined && points[index]?.count > 0) onPickDay(points[index].day);
                                },
                                onHover: (event, elements) => {
                                    const target = event.native?.target as HTMLElement | undefined;
                                    if (target) target.style.cursor = points[elements[0]?.index]?.count > 0 ? 'pointer' : 'default';
                                },
                                plugins: {
                                    legend: { display: false },
                                    tooltip: {
                                        callbacks: {
                                            title: (items) => {
                                                const index = items[0]?.dataIndex ?? 0;
                                                return points[index] ? displayDay(points[index].day) : '';
                                            },
                                            label: (item) => {
                                                // O anterior mostra a própria data: "anterior" sozinho não diz o dia comparado.
                                                const day = mode === 'total' && item.datasetIndex === 1 && previous[item.dataIndex]
                                                    ? ` (${displayDay(previous[item.dataIndex].day)})`
                                                    : '';
                                                return ` ${item.dataset.label}${day}: ${item.raw}`;
                                            },
                                        },
                                    },
                                },
                                scales: {
                                    x: {
                                        grid: { display: false },
                                        ticks: { color: cc.textMuted, maxRotation: 0, autoSkipPadding: 24 },
                                    },
                                    y: {
                                        beginAtZero: true,
                                        // Lead é contagem: meio lead não existe.
                                        ticks: { color: cc.textMuted, precision: 0 },
                                        grid: { color: cc.border },
                                    },
                                },
                            }}
                        />
                    </Box>
                </>
            )}
        </Panel>
    );
};
